// Character-customisation slot → folder category. This is the single source of truth
// for how avatar art is organised under assets/characters/<category>/, shared by:
//   - the build tooling (scripts/asset-paths.ts → where each file is placed)
//   - the decoders (which emit the public paths into the manifests)
//   - the chibi renderer (clients/web ChibiAvatar/NpcChibi, which build paths in code)
// so the on-disk/R2 layout and the paths the renderers request can never drift apart.
//
// Folders are fine-grained — one per customizer slot — so adding a new item means
// dropping its art (chibi `c{m,w}_<slot>_…` + portrait `w{m,w}_<slot>_…`) into a single
// intuitive folder. Leaf filenames are unchanged (renderers parse them).
/** Slot mnemonic → category folder name (matches the in-game customizer tabs). */
export const SLOT_CATEGORY = {
    h1: "front-hair",
    h2: "back-hair",
    hs: "hair-style",
    he: "hair-style",
    bo: "body",
    dc: "outfit",
    ac: "costume",
    dd: "disks",
    da: "disks",
    ey: "eyes",
    no: "nose",
    mo: "mouth",
    eb: "eyebrows",
    es: "eye-detail",
    a1: "accessory",
    ad: "accessory",
    a2: "headwear",
    sh: "base",
    atari: "base",
};
/** Slot mnemonics, longest first, for greedy prefix-matching a (possibly fused) filename token. */
const KNOWN_SLOTS = Object.keys(SLOT_CATEGORY).sort((a, b) => b.length - a.length);
/** Category folder for a clean slot mnemonic (what the chibi renderer holds). */
export function slotCategory(slot) {
    return SLOT_CATEGORY[slot] ?? "misc";
}
/** Extract the slot mnemonic from an avatar leaf filename, e.g.
 *   "wm_dd_01_c01_r.png" → "dd", "cm_bo01_c01_n01_01.png" → "bo" (fused slot+variant).
 *  Returns null for non-avatar / NPC-preset names. */
export function slotFromFilename(name) {
    // gender prefix (cm/cw player chibi, wm/ww portrait) + the token up to the next `_`.
    const m = name.match(/^(?:c[mw]|w[mw])_([a-z0-9]+)_/);
    if (!m)
        return null;
    const tok = m[1];
    for (const s of KNOWN_SLOTS)
        if (tok === s || tok.startsWith(s))
            return s;
    return null;
}
/** Public folder (under characters/) for an avatar leaf filename, or null if unrecognised.
 *  NPC chibi presets (`cn_…`) all live together under `npc`. */
export function characterFolder(name) {
    if (name.startsWith("cn_"))
        return "npc";
    const slot = slotFromFilename(name);
    return slot ? slotCategory(slot) : null;
}
// The waist-up character-creator set (TestData/waistup) uses its OWN slot prefixes (`mb`, `mh1`,
// `mc1`, …), deduced from canvas position: top = hair, mid = face, bottom = body/clothes. Folded
// into the SAME category folders as the portrait + chibi art so each item's art across ALL three
// render contexts colocates. `mfi`/`mr`/`md` (forehead / upper / glasses) are best-effort — they
// render fine wherever they sit (the editor reads paths from the manifest); adjust here to retag.
export const EDITOR_CATEGORY = {
    mb: "body",
    mc1: "outfit",
    mc2: "outfit",
    mh1: "front-hair",
    mh3: "front-hair",
    mh4: "front-hair",
    mh2: "back-hair",
    mhs: "hair-style",
    mn: "nose",
    mm: "mouth",
    mi1: "eyes",
    mi2: "eyes",
    mis: "eye-detail",
    mfi: "eyebrows",
    mr: "headwear",
    md: "accessory",
};
/** Category folder for a waist-up editor leaf filename (e.g. "mh1_01_c01.png" → "front-hair"). */
export function editorFolder(name) {
    const m = name.match(/^([a-z]+\d*)_/);
    return (m && EDITOR_CATEGORY[m[1]]) ?? "misc";
}
//# sourceMappingURL=slots.js.map